import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import StatusBadge from '../../atoms/StatusBadge/StatusBadge';
import { DATE_TIME_FORMAT_FULL } from '../../utils/dates';
import useBinControllerSensorStatus from '../../utils/hooks/useBinControllerSensorStatus';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor';

const LAN_STATUS_LABELS = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  unknown: 'Unknown',
};

const LAN_STATUS_BADGE = {
  connected: 'success',
  disconnected: 'error',
  unknown: 'warning',
};

const UPLOAD_STATUS_LABELS = {
  success: 'OK',
  stale: 'Stale',
  failed: 'Failed',
  unknown: 'Unknown',
};

const UPLOAD_STATUS_BADGE = {
  success: 'success',
  stale: 'warning',
  failed: 'error',
  unknown: 'warning',
};

function formatRelativeTime(timestamp) {
  if (timestamp == null) {
    return '—';
  }
  return dayjs.tz(Number(timestamp) * 1000).fromNow();
}

function formatFullTime(timestamp) {
  if (timestamp == null) {
    return 'Unknown';
  }
  return dayjs.tz(Number(timestamp) * 1000).format(DATE_TIME_FORMAT_FULL);
}

function buildLanEventTooltip(lastEvent) {
  if (!lastEvent) {
    return 'No LAN connect/disconnect events since last reset';
  }

  const lines = [
    lastEvent.name,
    formatFullTime(lastEvent.startedAt),
  ];

  if (lastEvent.reason) {
    lines.push(`Reason: ${lastEvent.reason}`);
  }
  if (lastEvent.ip) {
    lines.push(`IP: ${lastEvent.ip}`);
  }

  return lines.join('\n');
}

function buildUploadTooltip(lastTransactionAt, lastTransactionStatus) {
  if (lastTransactionAt == null) {
    return 'No upload transactions recorded';
  }

  return [
    formatFullTime(lastTransactionAt),
    `HTTP status: ${lastTransactionStatus ?? 'unknown'}`,
  ].join('\n');
}

export default function BinControllerSensorStatusSection({ deviceId }) {
  const { censor } = useCensor();
  const { loading, error, devices, summary } = useBinControllerSensorStatus({ deviceId });

  if (loading && devices.length === 0) {
    return <div className="DeviceCard-sensorStatus">Loading connected sensors…</div>;
  }

  if (error) {
    return <div className="DeviceCard-sensorStatus">Failed to load connected sensor status.</div>;
  }

  if (!summary || devices.length === 0) {
    return <div className="DeviceCard-sensorStatus">No feed line sensors configured for this bin controller.</div>;
  }

  return (
    <div className="DeviceCard-sensorStatus">
      <div className="DeviceCard-sensorStatus-summary">
        {summary.connectedCount} connected, {summary.disconnectedCount} disconnected, {summary.unknownCount} unknown
        {summary.unassignedCount > 0 ? ` (${summary.unassignedCount} unassigned)` : ''}
      </div>
      <div className="DeviceCard-sensorStatus-header DeviceCard-sensorStatus-row">
        <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--serial">Serial</span>
        <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--feedLine">Feed line</span>
        <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--lan">LAN</span>
        <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--lanEvent">Last LAN event</span>
        <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--upload">Last upload</span>
        <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--uploadOk">Upload OK</span>
      </div>
      {devices.map((device) => {
        const feedLineLabel = device.isUnassigned
          ? 'Unassigned'
          : censor(device.feedLineName, censorTypes.feedline) || 'Unknown';

        return (
          <div
            key={device.serial}
            className={`DeviceCard-sensorStatus-row${device.isUnassigned ? ' DeviceCard-sensorStatus-row--unassigned' : ''}`}
          >
            <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--serial">{device.serial}</span>
            <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--feedLine">{feedLineLabel}</span>
            <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--lan">
              <StatusBadge
                status={LAN_STATUS_BADGE[device.lanStatus] ?? 'warning'}
                text={LAN_STATUS_LABELS[device.lanStatus] ?? device.lanStatus}
              />
            </span>
            <span
              className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--lanEvent"
              title={buildLanEventTooltip(device.lastEvent)}
            >
              {formatRelativeTime(device.lastEvent?.startedAt)}
            </span>
            <span
              className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--upload"
              title={buildUploadTooltip(device.lastTransactionAt, device.lastTransactionStatus)}
            >
              {formatRelativeTime(device.lastTransactionAt)}
            </span>
            <span className="DeviceCard-sensorStatus-cell DeviceCard-sensorStatus-cell--uploadOk">
              <StatusBadge
                status={UPLOAD_STATUS_BADGE[device.uploadStatus] ?? 'warning'}
                text={UPLOAD_STATUS_LABELS[device.uploadStatus] ?? device.uploadStatus}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

BinControllerSensorStatusSection.propTypes = {
  deviceId: PropTypes.string.isRequired,
};
